<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UCContractServiceItemNameEditor',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: Array,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItems = (...args) => {
    const value = props.modelValue;
    if (value == null || value.length == 0) {
      return [];
    }
    return value;
  };
  const addItem = (...args) => {
    const newItems = [...items.value, newItem()];
    items.value = newItems;
    emit('update:modelValue', newItems);
  };
  const newItem = (...args) => {
    return {
      name: null,
      include_period: null,
      period_format: null,
      language: null,
    };
  };
  const removeItem = (...args) => {
    const [item] = args;
    const newItems = items.value.filter((p) => p !== item);
    if (newItems.length !== items.value.length) {
      items.value = newItems;
      emit('update:modelValue', newItems);
    }
  };

  const periodFormats = ref(null);
  const languages = ref([
    {
      value: 'hu',
      name: $fn.value.pgettext('languages', 'hungarian'),
    },
    {
      value: 'it',
      name: $fn.value.pgettext('languages', 'italian'),
    },
    {
      value: 'en',
      name: $fn.value.pgettext('languages', 'english'),
    },
  ]);
  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    periodFormats: periodFormats.value,
    languages: languages.value,
    items: items.value,
    ux: ux.value,
  }));

  const $$load_periodFormats = () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'billing_period_formats',
    });
  };
  const $$load_items = () => {
    return getItems();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'periodFormats' },
      { name: 'languages' },
      { name: 'items' },
      { name: 'ux' },
    ],
    loaders: { periodFormats: $$load_periodFormats, items: $$load_items },
    ux: {},
    dataRefs: { periodFormats, languages, items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );

  const act_4207795475682514_script_8a2oao97f_value_callable = async (
    $event
  ) => {
    return removeItem($event.scope.item);
  };
  const act_4207795475682514_script_8a2oao97f_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4207795475682514_script_8a2oao97f_value_callable($event);
      },
    };
  };
  const act_1040167445267876_script_hi3m40e3d_value_callable = async (
    $event
  ) => {
    return addItem();
  };
  const act_1040167445267876_script_hi3m40e3d_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_hi3m40e3d_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_1ov4k53z6 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_t13nxxe9n =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1ov4k53z6,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_t13nxxe9n],
    event: 'reload',
  });
  const act_4207795475682514_script_8a2oao97f = new actionTypes.ScriptAction({
    actionArgs: act_4207795475682514_script_8a2oao97f_getActionArgs,
  });
  const inst_evh_4207795475682514_clickPreventStop_r0uiua7lw =
    new actionTypes.EventHandlerImpl({
      action: act_4207795475682514_script_8a2oao97f,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_4207795475682514_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4207795475682514_clickPreventStop_r0uiua7lw],
      event: 'click.prevent.stop',
    });
  const act_1040167445267876_script_hi3m40e3d = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_hi3m40e3d_getActionArgs,
    displayName: 'addItem',
  });
  const inst_evh_1040167445267876_clickPreventStop_5eidw6wcu =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_hi3m40e3d,
      event: 'click.prevent.stop',
      displayName: 'addItem',
    });
  const inst_evh_1040167445267876_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_clickPreventStop_5eidw6wcu],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_t13nxxe9n,
    inst_evh_7315092382398562_reload,
    inst_evh_4207795475682514_clickPreventStop_r0uiua7lw,
    inst_evh_4207795475682514_clickPreventStop,
    inst_evh_1040167445267876_clickPreventStop_5eidw6wcu,
    addItem: inst_evh_1040167445267876_clickPreventStop_5eidw6wcu,
    inst_evh_1040167445267876_clickPreventStop,
  };

  const evh_7315092382398562_reload_t13nxxe9n = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_t13nxxe9n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4207795475682514_clickPreventStop_r0uiua7lw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4207795475682514_clickPreventStop_r0uiua7lw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4207795475682514_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4207795475682514_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop_5eidw6wcu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop_5eidw6wcu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div
      :key="index"
      class="d-flex flex-col row"
      v-for="(item, index) in items"
    >
      <div class="col-auto" v-if="items.length > 1">#{{ index + 1 }}</div>
      <div class="col">
        <div class="mb-2">
          <ItpFormInput
            name="name"
            v-model="item.name"
            required
            :readonly="readonly"
          >
          </ItpFormInput>
        </div>
        <div class="row align-items-center">
          <div class="col-auto">
            <ItpFormCheckbox
              name="include_period"
              v-model="item.include_period"
              :readonly="readonly"
              :label="
                pgettext('ContractServiceItemNameEditor', 'Include period')
              "
              class="form-check form-check-inline"
            >
            </ItpFormCheckbox>
          </div>
          <div class="col">
            <ItpFormNativeSelect
              name="period_format"
              v-model="item.period_format"
              :options="periodFormats"
              :readonly="readonly"
              valueField="value"
              textField="name"
              null-option
              :disabled="!item.include_period"
            >
            </ItpFormNativeSelect>
          </div>
          <div class="col-auto">
            <ItpFormNativeSelect
              name="language"
              v-model="item.language"
              :options="languages"
              :readonly="readonly"
              valueField="value"
              textField="name"
              null-option
              :disabled="!item.include_period"
            >
            </ItpFormNativeSelect>
          </div>
        </div>
        <hr class="w-25" v-if="index != items.length - 1" />
      </div>
      <div class="col-auto" v-if="!readonly && !disabled">
        <button
          data-bs-dismiss="modal"
          aria-label="Close"
          :title="pgettext('ContractServiceItemNameEditor', 'Remove name')"
          class="close"
          @click.prevent.stop="
            evh_4207795475682514_clickPreventStop($event, { item, index })
          "
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
    <div class="d-flex mt-2 align-items-center" v-if="!readonly && !disabled">
      <div
        class="col-auto"
        @click.prevent.stop="evh_1040167445267876_clickPreventStop($event, {})"
      >
        <ItpLink
          :icon="$config.addIcon"
          :text="pgettext('ContractServiceItemNameEditor', 'Add name')"
        >
        </ItpLink>
      </div>
    </div>
  </ItpUsercontrol>
</template>
